import { useCallback } from 'react';

import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import { canOfferBeRedeemed } from 'components/CancelSubscription/Offers/offers';

import {
  GetRedeemedOffersDocument,
  RedeemedOfferType,
  RedeemedOfferVariant,
  useGetRedeemedOffersQuery,
  useReactivateSubscriptionMutation,
  useRedeemOfferMutation,
} from 'graphql/generated/graphql';

import { localizeWithCurrentUrl } from 'helpers/locales';
import { isInTrial } from 'helpers/subscription';

import { useAuth } from 'hooks/useAuth';
import useGraphaelDefaults from 'hooks/useGraphaelDefaults';
import { useTracking } from 'hooks/useTracking';

import { setProfileUser } from 'store/modules/profile';

interface IProps {
  applyCoupon: boolean;
  trackingName: string;
}

const useReactivateTrialSubscription = ({
  applyCoupon,
  trackingName,
}: IProps) => {
  const { push } = useHistory();
  const { user } = useAuth();
  const { activeSubscription } = useAuth();

  const graphaelDefaults = useGraphaelDefaults();

  const { data: dataRedeemedOffers } = useGetRedeemedOffersQuery({
    ...graphaelDefaults,
    fetchPolicy: 'cache-and-network',
  });

  const { trackContentInteraction } = useTracking();

  const dispatch = useDispatch();

  const [
    reactivateSubscription,
    { loading: reactivateTrialSubscriptionLoading },
  ] = useReactivateSubscriptionMutation({
    ...graphaelDefaults,
  });

  const [redeemOffer, { loading: redeemOfferLoading }] = useRedeemOfferMutation(
    {
      ...graphaelDefaults,
    },
  );

  const reactivateTrialSubscription = useCallback(async () => {
    const { data: reactivateSubscriptionData } = await reactivateSubscription();

    if (
      applyCoupon &&
      canOfferBeRedeemed(
        RedeemedOfferType.Discount,
        dataRedeemedOffers?.redeemedOffers,
      ) &&
      reactivateSubscriptionData?.reactivateSubscription?.id
    ) {
      await redeemOffer({
        variables: {
          redeemOfferInput: {
            offerType: RedeemedOfferType.Discount,
            variant: RedeemedOfferVariant.ScheduleForCancellation,
          },
        },
        refetchQueries: [
          {
            query: GetRedeemedOffersDocument,
            context: graphaelDefaults.context,
          },
        ],
      });
    }

    if (reactivateSubscriptionData?.reactivateSubscription && user) {
      dispatch(
        setProfileUser({
          ...user,
          activeSubscription: {
            ...user?.activeSubscription,
            cancellationScheduledAt:
              reactivateSubscriptionData.reactivateSubscription
                .activeSubscription?.cancellationScheduledAt,
          },
        } as typeof user),
      );

      push(
        localizeWithCurrentUrl(
          '/my-femtasy/account/subscription-reactivation-success',
        ),
      );
    }

    trackContentInteraction(
      'click',
      trackingName,
      `reactivate-subscription${applyCoupon ? '-with-coupon' : ''}`,
    );
  }, [
    applyCoupon,
    dataRedeemedOffers?.redeemedOffers,
    dispatch,
    graphaelDefaults.context,
    push,
    reactivateSubscription,
    redeemOffer,
    trackContentInteraction,
    trackingName,
    user,
  ]);

  return {
    canReactivateTrialSubscription:
      !!activeSubscription?.cancellationScheduledAt &&
      isInTrial(activeSubscription.freeTrialEndsAt),
    reactivateTrialSubscription,
    reactivateTrialSubscriptionLoading:
      redeemOfferLoading || reactivateTrialSubscriptionLoading,
  };
};

export default useReactivateTrialSubscription;
